import React from 'react';
import { translateEvent } from '../utils/gameUtils';
import { getEffectiveBatterSpeed } from './cardHelpers';

// ── Styled phrase mapping for result descriptions ──

const STYLED_PHRASES = {
    'The pitcher looked tired.': 'fatigue-text',
    'The pitcher appeared to be nursing an injury.': 'injury-text',
    'The heat seemed to affect the pitcher.': 'weather-hot-text',
    'The cold seemed to affect the pitcher.': 'weather-cold-text',
    'The altitude seemed to favor the batter.': 'altitude-text',
};

export const formatResultDescription = (description) => {
    if (!description) return null;
    const phrases = Object.keys(STYLED_PHRASES);
    const regex = new RegExp(`(${phrases.map(p => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'g');
    const parts = description.split(regex);

    return parts.map((part, index) => {
        const className = STYLED_PHRASES[part];
        if (className) {
            return <span key={index} className={className}>{part}</span>;
        }
        return <React.Fragment key={index}>{part}</React.Fragment>;
    });
};

// ── Runner builder for the diamond display ──
// Variation/upgrades are sourced from the lineup's own CardInventory — never
// cross-referenced by cardID against the current user's inventory. That would
// leak the viewer's variation onto opponent cards (and hide the opponent's).

export const makeRunner = (lineup, lineupID, userIsPitching) => {
    if (!lineupID) return null;
    const inventory = lineup?.CardInventory ?? null;
    return {
        name: lineup?.Card?.name ?? null,
        speed: getEffectiveBatterSpeed(lineup?.Card, inventory?.upgrades),
        isUser: !userIsPitching,
        variationCss: inventory?.CardVariation?.cssClass || null,
        variationName: inventory?.CardVariation?.name || null,
    };
};

// Build the display card object from a GameLineup, hoisting the lineup's own
// CardInventory onto the card so downstream components (Card.js, useCardImage,
// usePitcherFatigue) get the correct owner's variation / upgrades / displayKey.

export const cardFromLineup = (lineup) => {
    if (!lineup?.Card) return null;
    return { ...lineup.Card, CardInventory: lineup.CardInventory ?? null };
};

// ── Derived state from the latest game event ──

export const deriveResultState = (revealPhase, latestEvent, userIsPitching, eventUserIsPitchingRef) => {
    const strikeThroughH = latestEvent.resultType !== latestEvent.origResultType;
    const strikeThroughP = latestEvent.resultType === latestEvent.origResultType;

    const runnersOnBase = !!(
        latestEvent.firstBaseRunnerLineupID ||
        latestEvent.secondBaseRunnerLineupID ||
        latestEvent.thirdBaseRunnerLineupID
    );

    const showResultPopup = revealPhase
        && !['pending', 'batting', 'pitching', 'done'].includes(revealPhase)
        && latestEvent?.resultType;

    const resultOnPitcherCard = showResultPopup
        && ['so', 'oo'].includes(latestEvent.resultType?.toLowerCase());

    const popupUserIsPitching = revealPhase && revealPhase !== 'done'
        ? eventUserIsPitchingRef.current
        : userIsPitching;

    const resultIsGoodForUser = resultOnPitcherCard ? popupUserIsPitching : !popupUserIsPitching;

    const resultIsXBH = showResultPopup
        && ['h2', 'h3', 'hr'].includes(latestEvent.resultType?.toLowerCase());

    const resultPopupText = showResultPopup ? translateEvent(latestEvent.resultType).event : null;

    const eventWasModified = showResultPopup
        && latestEvent.origResultType
        && latestEvent.origResultType !== latestEvent.resultType;

    const origPopupText = eventWasModified ? translateEvent(latestEvent.origResultType).event : null;

    return {
        strikeThroughH,
        strikeThroughP,
        runnersOnBase,
        showResultPopup,
        resultOnPitcherCard,
        resultIsGoodForUser,
        resultIsXBH,
        resultPopupText,
        eventWasModified,
        origPopupText,
    };
};
